import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { StaffRail } from '@/components/layout/StaffRail';
import { api, getAccessToken, API } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ResponsiveContainer, AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip, BarChart, Bar, PieChart, Pie, Cell, Legend } from 'recharts';
import { Download, LogOut, RefreshCw, FileText, Users } from 'lucide-react';
import { toast } from 'sonner';
import { fmtMoney } from '@/lib/realtime';

const PIE_COLORS = ['#c55a3d', '#1f7a4d', '#c8a24a', '#7a5af8', '#b42318'];

function todayStr() { return new Date().toISOString().slice(0, 10); }
function addDays(d, n) { const t = new Date(d); t.setDate(t.getDate() + n); return t.toISOString().slice(0, 10); }

export default function ReportsPage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState(null);
  const [series, setSeries] = useState([]);
  const [best, setBest] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [today, setToday] = useState(null);
  const [allTime, setAllTime] = useState(null);
  // Past tab — custom date range (from/to)
  const [pastFrom, setPastFrom] = useState(addDays(todayStr(), -7));
  const [pastTo, setPastTo] = useState(todayStr());
  const [past, setPast] = useState(null);
  const [pastLoading, setPastLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, sb, bs, fs, td, at] = await Promise.all([
        api.get('/reports/sales-summary', { params: { days } }),
        api.get('/admin/stats/sales-by-day', { params: { days } }),
        api.get('/admin/stats/best-sellers', { params: { limit: 10 } }),
        api.get('/feedback/summary'),
        api.get('/reports/today'),
        api.get('/reports/all-time'),
      ]);
      setSummary(s.data);
      setSeries(sb.data?.series || []);
      setBest(bs.data?.items || []);
      setFeedback(fs.data);
      setToday(td.data);
      setAllTime(at.data);
    } catch (e) { toast.error('Failed to load reports'); }
  }, [days]);
  useEffect(() => { load(); }, [load]);

  const loadPast = useCallback(async () => {
    if (!pastFrom || !pastTo) return;
    if (pastFrom > pastTo) { toast.error('“From” must be before “To”'); return; }
    setPastLoading(true);
    try {
      const { data } = await api.get('/reports/range', { params: { date_from: pastFrom, date_to: pastTo } });
      setPast(data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load past sales'); }
    finally { setPastLoading(false); }
  }, [pastFrom, pastTo]);
  useEffect(() => { loadPast(); }, [loadPast]);

  const quickRange = (n) => { setPastFrom(addDays(todayStr(), -n + 1)); setPastTo(todayStr()); };

  const downloadCsv = async () => {
    try {
      const token = getAccessToken();
      const res = await fetch(`${API}/reports/orders.csv?days=${days}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { toast.error('Download failed'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `city_grill_orders_${days}d.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (e) { toast.error('Failed'); }
  };

  const typeData = Object.entries(summary?.by_type || {}).map(([k, v]) => ({ name: k.replace('_', ' '), value: v.revenue }));
  const payData = Object.entries(summary?.by_payment || {}).map(([k, v]) => ({ name: k.toUpperCase(), value: v.revenue }));

  return (
    <div className="min-h-screen bg-background">
      <StaffRail />
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-card px-4 py-3 sm:px-6">
        <div>
          <h1 className="font-display text-lg font-semibold">Reports</h1>
          <p className="text-xs text-muted-foreground">Sales, payments, & feedback analytics</p>
        </div>
        <div className="flex items-center gap-2">
          <select data-testid="reports-days-select" value={days} onChange={(e) => setDays(Number(e.target.value))} className="h-9 rounded-md border border-input bg-background px-2 text-sm">
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last 365 days</option>
          </select>
          <Button data-testid="reports-refresh-button" size="sm" variant="secondary" onClick={load}><RefreshCw className="mr-1 h-4 w-4" /> Refresh</Button>
          <Button data-testid="reports-export-csv-button" size="sm" onClick={downloadCsv}><Download className="mr-1 h-4 w-4" /> Export CSV</Button>
          <Button size="sm" variant="secondary" onClick={() => nav('/server')}>Server</Button>
          <Button size="sm" variant="ghost" onClick={() => { logout(); }}><LogOut className="mr-1 h-4 w-4" /> Sign out</Button>
        </div>
      </header>
      <main className="mx-auto max-w-[1400px] space-y-4 px-4 py-6 sm:px-6">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
          <Kpi data-testid="reports-kpi-sales" label="Total sales" value={fmtMoney(summary?.total_sales || 0)} />
          <Kpi data-testid="reports-kpi-paid" label="Paid sales" value={fmtMoney(summary?.paid_sales || 0)} />
          <Kpi data-testid="reports-kpi-orders" label="Orders" value={summary?.order_count ?? 0} />
          <Kpi data-testid="reports-kpi-avg" label="Avg order" value={fmtMoney(summary?.avg_order || 0)} />
          <Kpi data-testid="reports-kpi-cancelled" label="Cancelled" value={summary?.cancelled_count ?? 0} />
        </div>

        <Tabs defaultValue="today">
          <TabsList>
            <TabsTrigger data-testid="reports-tab-today" value="today">Today</TabsTrigger>
            <TabsTrigger data-testid="reports-tab-past" value="past">Past sales</TabsTrigger>
            <TabsTrigger data-testid="reports-tab-alltime" value="alltime">All time</TabsTrigger>
            <TabsTrigger data-testid="reports-tab-sales" value="sales">Trend</TabsTrigger>
            <TabsTrigger data-testid="reports-tab-mix" value="mix">Mix</TabsTrigger>
            <TabsTrigger data-testid="reports-tab-feedback" value="feedback">Feedback</TabsTrigger>
            <TabsTrigger data-testid="reports-tab-payroll" value="payroll">Payroll</TabsTrigger>
          </TabsList>
          <TabsContent value="today">
            <div className="grid gap-3 md:grid-cols-4">
              <Kpi label="Today's sales" value={fmtMoney(today?.total_sales || 0)} />
              <Kpi label="Paid" value={fmtMoney(today?.paid_sales || 0)} />
              <Kpi label="Orders" value={today?.order_count ?? 0} />
              <Kpi label="Cancelled" value={today?.cancelled_count ?? 0} />
            </div>
            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              <Card><CardHeader><CardTitle>Hourly revenue today</CardTitle></CardHeader>
                <CardContent className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={today?.hourly || []}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                      <XAxis dataKey="hour" tick={{ fontSize: 11 }} tickFormatter={(h) => `${h}:00`} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="revenue" fill="hsl(var(--primary))" radius={4} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
              <Card><CardHeader><CardTitle>Today by payment method</CardTitle></CardHeader>
                <CardContent>
                  <ul className="divide-y divide-border">
                    {Object.entries(today?.by_payment || {}).map(([k, v]) => (
                      <li key={k} className="flex items-center justify-between py-2 text-sm">
                        <span className="uppercase">{k}</span>
                        <span className="tabular">{fmtMoney(v.revenue)} <span className="text-muted-foreground">({v.count})</span></span>
                      </li>
                    ))}
                    {!Object.keys(today?.by_payment || {}).length && <li className="py-4 text-center text-xs text-muted-foreground">No sales today yet.</li>}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          <TabsContent value="past">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
                <CardTitle>Past sales</CardTitle>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex items-center gap-1.5">
                    <Label className="text-xs text-muted-foreground" htmlFor="past-from">From</Label>
                    <Input data-testid="reports-past-from-input" id="past-from" type="date" max={pastTo} value={pastFrom} onChange={(e) => setPastFrom(e.target.value)} className="h-9 w-40" />
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Label className="text-xs text-muted-foreground" htmlFor="past-to">To</Label>
                    <Input data-testid="reports-past-to-input" id="past-to" type="date" min={pastFrom} max={todayStr()} value={pastTo} onChange={(e) => setPastTo(e.target.value)} className="h-9 w-40" />
                  </div>
                  <div className="flex items-center gap-1 border-l border-border pl-2">
                    <Button data-testid="reports-past-quick-7" size="sm" variant="secondary" onClick={() => quickRange(7)}>7d</Button>
                    <Button data-testid="reports-past-quick-30" size="sm" variant="secondary" onClick={() => quickRange(30)}>30d</Button>
                    <Button data-testid="reports-past-quick-90" size="sm" variant="secondary" onClick={() => quickRange(90)}>90d</Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground">{pastLoading ? 'Loading…' : `Range: ${past?.date_from || pastFrom} → ${past?.date_to || pastTo}`}</p>
                <div className="mt-3 grid gap-3 md:grid-cols-4">
                  <Kpi data-testid="reports-past-kpi-sales" label="Range sales" value={fmtMoney(past?.total_sales || 0)} />
                  <Kpi data-testid="reports-past-kpi-paid" label="Paid" value={fmtMoney(past?.paid_sales || 0)} />
                  <Kpi data-testid="reports-past-kpi-orders" label="Orders" value={past?.order_count ?? 0} />
                  <Kpi data-testid="reports-past-kpi-avg" label="Avg order" value={fmtMoney(past?.avg_order || 0)} />
                </div>
                <div className="mt-4 h-80">
                  {!(past?.series || []).length ? (
                    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">No sales in this range.</div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={past?.series || []}>
                        <defs>
                          <linearGradient id="rpast" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.5} />
                            <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Area type="monotone" dataKey="sales" stroke="hsl(var(--primary))" fill="url(#rpast)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </div>
                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  <Card>
                    <CardHeader><CardTitle className="text-base">By order type</CardTitle></CardHeader>
                    <CardContent>
                      <ul className="divide-y divide-border">
                        {Object.entries(past?.by_type || {}).map(([k, v]) => (
                          <li key={k} className="flex items-center justify-between py-1.5 text-sm">
                            <span className="capitalize">{k.replace('_', ' ')}</span>
                            <span className="tabular">{fmtMoney(v.revenue)} <span className="text-muted-foreground">({v.count})</span></span>
                          </li>
                        ))}
                        {!Object.keys(past?.by_type || {}).length && <li className="py-3 text-center text-xs text-muted-foreground">—</li>}
                      </ul>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader><CardTitle className="text-base">By payment method</CardTitle></CardHeader>
                    <CardContent>
                      <ul className="divide-y divide-border">
                        {Object.entries(past?.by_payment || {}).map(([k, v]) => (
                          <li key={k} className="flex items-center justify-between py-1.5 text-sm">
                            <span className="uppercase">{k}</span>
                            <span className="tabular">{fmtMoney(v.revenue)} <span className="text-muted-foreground">({v.count})</span></span>
                          </li>
                        ))}
                        {!Object.keys(past?.by_payment || {}).length && <li className="py-3 text-center text-xs text-muted-foreground">—</li>}
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="alltime">
            <div className="grid gap-3 md:grid-cols-4">
              <Kpi label="Lifetime sales" value={fmtMoney(allTime?.total_sales || 0)} />
              <Kpi label="Lifetime paid" value={fmtMoney(allTime?.paid_sales || 0)} />
              <Kpi label="Lifetime orders" value={allTime?.order_count ?? 0} />
              <Kpi label="Avg order" value={fmtMoney(allTime?.avg_order || 0)} />
            </div>
            <p className="mt-2 text-xs text-muted-foreground">Since first order: {allTime?.first_order_date ? new Date(allTime.first_order_date).toLocaleDateString() : '—'}</p>
            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              <Card><CardHeader><CardTitle>Monthly revenue</CardTitle></CardHeader>
                <CardContent className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={allTime?.by_month || []}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="revenue" fill="hsl(var(--accent))" radius={4} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
              <Card><CardHeader><CardTitle>Lifetime top items</CardTitle></CardHeader>
                <CardContent>
                  <ul className="divide-y divide-border">
                    {(allTime?.top_items || []).map((it) => (
                      <li key={it.menu_item_id} className="flex items-center justify-between py-2 text-sm">
                        <span>{it.name}</span>
                        <span className="tabular">{it.quantity} sold · {fmtMoney(it.revenue)}</span>
                      </li>
                    ))}
                    {!(allTime?.top_items || []).length && <li className="py-4 text-center text-xs text-muted-foreground">No data yet.</li>}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          <TabsContent value="sales">
            <div className="grid gap-4 lg:grid-cols-3">
              <Card className="lg:col-span-2">
                <CardHeader><CardTitle>Sales over time</CardTitle></CardHeader>
                <CardContent className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={series}>
                      <defs>
                        <linearGradient id="r1" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.5} />
                          <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Area type="monotone" dataKey="sales" stroke="hsl(var(--primary))" fill="url(#r1)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle>Top items</CardTitle></CardHeader>
                <CardContent className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={best.slice(0, 8)} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Bar dataKey="quantity" fill="hsl(var(--accent))" radius={4} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          <TabsContent value="mix">
            <div className="grid gap-4 lg:grid-cols-2">
              <Card>
                <CardHeader><CardTitle>Revenue by order type</CardTitle></CardHeader>
                <CardContent className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={typeData} dataKey="value" nameKey="name" outerRadius={90} label>
                        {typeData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip /><Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle>Revenue by payment method</CardTitle></CardHeader>
                <CardContent className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={payData} dataKey="value" nameKey="name" outerRadius={90} label>
                        {payData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip /><Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          <TabsContent value="feedback">
            <Card>
              <CardHeader><CardTitle>Customer feedback summary</CardTitle></CardHeader>
              <CardContent>
                <div className="grid gap-3 sm:grid-cols-4">
                  <FRow label="Food" value={feedback?.avg_food ?? 0} />
                  <FRow label="Service" value={feedback?.avg_service ?? 0} />
                  <FRow label="Cleanliness" value={feedback?.avg_cleanliness ?? 0} />
                  <FRow label="Delivery" value={feedback?.avg_delivery ?? 0} />
                </div>
                <p className="mt-4 text-xs text-muted-foreground">Based on {feedback?.count || 0} ratings</p>
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="payroll">
            <PayrollPanel />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function Kpi({ label, value, ...rest }) {
  return (
    <Card {...rest}>
      <CardContent className="p-4">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
        <p className="mt-1 font-display text-2xl font-semibold tabular">{value}</p>
      </CardContent>
    </Card>
  );
}
function FRow({ label, value }) {
  return (
    <Card><CardContent className="p-4 text-center">
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="mt-2 font-display text-3xl font-semibold tabular text-primary">{Number(value).toFixed(2)}<span className="text-base text-muted-foreground">/5</span></p>
    </CardContent></Card>
  );
}

// ============================================================
// PAYROLL ATTENDANCE PANEL — HR-ready hours / days breakdown + PDF export
// ============================================================
function ymdLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}
function addDaysLocal(d, n) { const t = new Date(d); t.setDate(t.getDate() + n); return t; }
function startOfWeekLocal(d) { const t = new Date(d); t.setHours(0, 0, 0, 0); t.setDate(t.getDate() - t.getDay()); return t; }
function startOfMonthLocal(d) { const t = new Date(d); t.setDate(1); t.setHours(0, 0, 0, 0); return t; }
function endOfMonthLocal(d) { const t = new Date(d); t.setMonth(t.getMonth() + 1, 0); t.setHours(23, 59, 59, 999); return t; }

const PERIOD_PRESETS = [
  // Week
  { id: 'this_week',     label: 'This week',     group: 'Week',     compute: () => { const s = startOfWeekLocal(new Date()); const e = addDaysLocal(s, 6); return [s, e]; } },
  { id: 'last_week',     label: 'Last week',     group: 'Week',     compute: () => { const s = startOfWeekLocal(addDaysLocal(new Date(), -7)); const e = addDaysLocal(s, 6); return [s, e]; } },
  // Bi-weekly
  { id: 'last_14',       label: 'Last 14 days',  group: 'Bi-weekly',compute: () => { const e = new Date(); const s = addDaysLocal(e, -13); return [s, e]; } },
  { id: 'prev_14',       label: 'Previous 14 d', group: 'Bi-weekly',compute: () => { const e = addDaysLocal(new Date(), -14); const s = addDaysLocal(e, -13); return [s, e]; } },
  // Monthly
  { id: 'this_month',    label: 'This month',    group: 'Monthly',  compute: () => [startOfMonthLocal(new Date()), endOfMonthLocal(new Date())] },
  { id: 'last_month',    label: 'Last month',    group: 'Monthly',  compute: () => { const now = new Date(); const s = new Date(now.getFullYear(), now.getMonth() - 1, 1); const e = endOfMonthLocal(s); return [s, e]; } },
];

function PayrollPanel() {
  const initial = PERIOD_PRESETS.find((p) => p.id === 'last_14').compute();
  const [from, setFrom] = useState(ymdLocal(initial[0]));
  const [to, setTo] = useState(ymdLocal(initial[1]));
  const [activePreset, setActivePreset] = useState('last_14');
  const [branchId, setBranchId] = useState('all');
  const [branches, setBranches] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(null); // user_id | 'summary' | 'all-detail'

  // Load branches once
  useEffect(() => {
    api.get('/branches').then((r) => setBranches(r.data || [])).catch(() => {});
  }, []);

  const applyPreset = (p) => {
    const [s, e] = p.compute();
    setFrom(ymdLocal(s));
    setTo(ymdLocal(e));
    setActivePreset(p.id);
  };

  const load = useCallback(async () => {
    if (!from || !to) return;
    if (from > to) { toast.error('"From" must be before "To"'); return; }
    setLoading(true);
    try {
      const params = { date_from: from, date_to: to };
      if (branchId && branchId !== 'all') params.branch_id = branchId;
      const { data } = await api.get('/reports/payroll', { params });
      setData(data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load payroll'); }
    finally { setLoading(false); }
  }, [from, to, branchId]);
  useEffect(() => { load(); }, [load]);

  // Download PDF via authenticated fetch — open in new tab (works inside preview iframes too)
  const downloadPdf = async ({ mode, user_id = null, label = null }) => {
    const key = user_id ? `user_${user_id}` : (mode === 'detail' ? 'all-detail' : 'summary');
    setExporting(key);
    // Some browsers (and the Emergent preview iframe) block popups unless we open a window
    // synchronously inside the click handler. Open it first, then navigate it once the blob is ready.
    const pendingWin = window.open('', '_blank');
    try {
      const token = getAccessToken();
      const qs = new URLSearchParams({ date_from: from, date_to: to, mode });
      if (user_id) qs.set('user_id', user_id);
      if (branchId && branchId !== 'all') qs.set('branch_id', branchId);
      const res = await fetch(`${API}/reports/payroll.pdf?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `HTTP ${res.status}`);
      }
      const cd = res.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename="([^"]+)"/);
      const fn = m ? m[1] : `payroll_${mode}_${from}_to_${to}.pdf`;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);

      // Strategy 1: Navigate the pre-opened tab to the PDF (works in iframes).
      if (pendingWin && !pendingWin.closed) {
        pendingWin.location.href = url;
      } else {
        // Strategy 2: fallback to an anchor click for direct download.
        const a = document.createElement('a');
        a.href = url;
        a.download = fn;
        a.target = '_blank';
        a.rel = 'noopener';
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        setTimeout(() => { document.body.removeChild(a); }, 250);
      }

      // Also offer an explicit download link in a toast (so user can save even if the
      // browser only previewed the PDF in the new tab).
      toast.success(
        `${label || 'PDF'} ready`,
        {
          description: 'Opened in a new tab — use your browser to save the PDF.',
          action: {
            label: 'Download',
            onClick: () => {
              const a = document.createElement('a');
              a.href = url; a.download = fn; a.target = '_blank'; a.rel = 'noopener';
              document.body.appendChild(a); a.click();
              setTimeout(() => document.body.removeChild(a), 250);
            },
          },
          duration: 12000,
        },
      );

      // Defer revocation so both the new tab and the optional download still work.
      setTimeout(() => URL.revokeObjectURL(url), 120000);
    } catch (e) {
      if (pendingWin && !pendingWin.closed) pendingWin.close();
      toast.error(e.message || 'PDF export failed');
    }
    finally { setExporting(null); }
  };

  const totals = data?.totals;
  const rows = data?.rows || [];
  const periodDays = data?.meta?.days_in_range ?? 0;

  const groupedPresets = PERIOD_PRESETS.reduce((acc, p) => { (acc[p.group] = acc[p.group] || []).push(p); return acc; }, {});

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3 flex-wrap">
        <div>
          <CardTitle className="flex items-center gap-2 text-base">
            <FileText className="h-5 w-5 text-primary" /> Payroll attendance
          </CardTitle>
          <p className="mt-1 text-xs text-muted-foreground">Hours, late, missed, PTO and sick — ready for HR processing. Export PDF per-staff or as a full summary.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="payroll-export-summary-button"
            variant="secondary"
            disabled={!rows.length || exporting === 'summary'}
            onClick={() => downloadPdf({ mode: 'summary', label: 'all-staff summary PDF' })}
          >
            <Users className="mr-2 h-4 w-4" />
            {exporting === 'summary' ? 'Exporting…' : 'Export summary PDF'}
          </Button>
          <Button
            data-testid="payroll-export-detail-button"
            disabled={!rows.length || exporting === 'all-detail'}
            onClick={() => downloadPdf({ mode: 'detail', label: 'all-staff detail PDF' })}
          >
            <Download className="mr-2 h-4 w-4" />
            {exporting === 'all-detail' ? 'Exporting…' : 'Export all detail PDFs'}
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Period picker */}
        <div className="rounded-lg border border-border bg-muted/30 p-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1.5">
              <Label className="text-xs text-muted-foreground" htmlFor="payroll-from">From</Label>
              <Input data-testid="payroll-from-input" id="payroll-from" type="date" value={from} max={to} onChange={(e) => { setFrom(e.target.value); setActivePreset(null); }} className="h-9 w-40" />
            </div>
            <div className="flex items-center gap-1.5">
              <Label className="text-xs text-muted-foreground" htmlFor="payroll-to">To</Label>
              <Input data-testid="payroll-to-input" id="payroll-to" type="date" value={to} min={from} max={ymdLocal(new Date())} onChange={(e) => { setTo(e.target.value); setActivePreset(null); }} className="h-9 w-40" />
            </div>
            <Badge variant="secondary" className="ml-1">{periodDays} day{periodDays === 1 ? '' : 's'}</Badge>
            {branches.length > 0 && (
              <div className="ml-auto flex items-center gap-1.5">
                <Label className="text-xs text-muted-foreground" htmlFor="payroll-branch">Branch</Label>
                <select
                  data-testid="payroll-branch-select"
                  id="payroll-branch"
                  value={branchId}
                  onChange={(e) => setBranchId(e.target.value)}
                  className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                >
                  <option value="all">All branches</option>
                  {branches.map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
            {Object.entries(groupedPresets).map(([group, presets]) => (
              <div key={group} className="flex flex-wrap items-center gap-1.5">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{group}:</span>
                {presets.map((p) => (
                  <button
                    key={p.id}
                    data-testid={`payroll-preset-${p.id}`}
                    onClick={() => applyPreset(p)}
                    className={`rounded-full border px-2.5 py-1 text-[11px] transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary ${activePreset === p.id ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-background hover:border-primary hover:text-primary'}`}
                  >{p.label}</button>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4 lg:grid-cols-6">
          <PayKpi data-testid="payroll-kpi-employees" label="Employees" value={totals?.employees ?? 0} tone="stone" />
          <PayKpi data-testid="payroll-kpi-scheduled" label="Scheduled" value={`${(totals?.scheduled_hours ?? 0).toFixed(1)} h`} tone="stone" />
          <PayKpi data-testid="payroll-kpi-worked" label="Worked" value={`${(totals?.worked_hours ?? 0).toFixed(1)} h`} tone="emerald" />
          <PayKpi data-testid="payroll-kpi-late" label="Late" value={`${totals?.late_count ?? 0} · ${(totals?.late_hours ?? 0).toFixed(1)}h`} tone="amber" />
          <PayKpi data-testid="payroll-kpi-missed" label="Missed" value={`${totals?.missed_count ?? 0} · ${(totals?.missed_hours ?? 0).toFixed(1)}h`} tone="rose" />
          <PayKpi data-testid="payroll-kpi-pto-sick" label="PTO + Sick" value={`${((totals?.pto_hours ?? 0) + (totals?.sick_hours ?? 0)).toFixed(1)} h`} tone="violet" />
        </div>

        {/* Table */}
        <div className="overflow-auto rounded border border-border">
          <table className="w-full min-w-[960px] text-sm" data-testid="payroll-table">
            <thead>
              <tr className="bg-muted/40 text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                <th className="p-2">Employee</th>
                <th className="p-2">Role</th>
                <th className="p-2 text-right">Sched (h)</th>
                <th className="p-2 text-right">Worked (h)</th>
                <th className="p-2 text-right">On time</th>
                <th className="p-2 text-right">Late</th>
                <th className="p-2 text-right">Missed</th>
                <th className="p-2 text-right">PTO (h)</th>
                <th className="p-2 text-right">Sick (h)</th>
                <th className="p-2 text-right">Att %</th>
                <th className="p-2 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={11} className="p-6 text-center text-sm text-muted-foreground">Loading payroll…</td></tr>
              )}
              {!loading && !rows.length && (
                <tr><td colSpan={11} className="p-6 text-center text-sm text-muted-foreground">No staff in this range.</td></tr>
              )}
              {!loading && rows.map((r) => (
                <tr key={r.user_id} data-testid="payroll-row" className="border-t border-border hover:bg-muted/20">
                  <td className="p-2 font-medium">{r.full_name}</td>
                  <td className="p-2 capitalize text-muted-foreground">{r.role}</td>
                  <td className="p-2 text-right tabular">{r.scheduled_hours.toFixed(2)}</td>
                  <td className="p-2 text-right tabular">{r.worked_hours.toFixed(2)}</td>
                  <td className="p-2 text-right tabular text-emerald-700">{r.on_time_count}</td>
                  <td className="p-2 text-right tabular">
                    <span className={r.late_count ? 'text-amber-700' : ''}>{r.late_count}</span>
                    {r.late_hours ? <span className="ml-1 text-[10px] text-muted-foreground">({r.late_hours.toFixed(2)}h)</span> : null}
                  </td>
                  <td className="p-2 text-right tabular">
                    <span className={r.missed_count ? 'text-rose-700' : ''}>{r.missed_count}</span>
                    {r.missed_hours ? <span className="ml-1 text-[10px] text-muted-foreground">({r.missed_hours.toFixed(2)}h)</span> : null}
                  </td>
                  <td className="p-2 text-right tabular">{r.pto_hours.toFixed(2)}</td>
                  <td className="p-2 text-right tabular">{r.sick_hours.toFixed(2)}</td>
                  <td className="p-2 text-right tabular">{r.on_time_rate == null ? '—' : `${r.on_time_rate.toFixed(0)}%`}</td>
                  <td className="p-2 text-right">
                    <Button
                      data-testid="payroll-row-pdf-button"
                      size="sm"
                      variant="secondary"
                      disabled={exporting === `user_${r.user_id}`}
                      onClick={() => downloadPdf({ mode: 'detail', user_id: r.user_id, label: `${r.full_name}'s payroll PDF` })}
                    >
                      <FileText className="mr-1 h-3.5 w-3.5" />
                      {exporting === `user_${r.user_id}` ? '…' : 'PDF'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-[11px] text-muted-foreground">
          Late threshold: 10 minutes after scheduled start. PTO and Sick are valued at 8 h/day for payroll purposes. Hours are decimal (1.50 = 1 h 30 min).
        </p>
      </CardContent>
    </Card>
  );
}

function PayKpi({ label, value, tone = 'stone' }) {
  const map = {
    emerald: 'bg-emerald-50 text-emerald-900 border-emerald-200',
    amber:   'bg-amber-50 text-amber-900 border-amber-200',
    rose:    'bg-rose-50 text-rose-900 border-rose-200',
    violet:  'bg-violet-50 text-violet-900 border-violet-200',
    stone:   'bg-stone-50 text-stone-900 border-stone-200',
  };
  return (
    <div className={`rounded border px-3 py-2 ${map[tone] || map.stone}`}>
      <p className="text-[10px] uppercase tracking-wider opacity-75">{label}</p>
      <p className="mt-0.5 font-display text-lg font-semibold tabular">{value}</p>
    </div>
  );
}

